import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Select,
  Input,
  Card,
  Row,
  Col,
  Modal,
  message,
  Space,
  DatePicker,
  Typography,
  Progress,
  Tooltip
} from 'antd';
import { DownloadOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { URL_API } from '@configs/';
import './style.css';
import { showError } from '@components/notification';
import { communityList, convertLocaltimeToUTC, formatCurrency } from '@utils/utils';
import {
  apiGetOriginalData,
  apiSyncData,
  apiSyncTimeData
} from '@services/ManageDevices/DeviceServices';
import { loadFromLocalStorageObjectFromBase64, saveToLocalStorage } from '@databases/localStorage';
import { contantAuthentication } from '@contants/';
import { get, isEmpty } from 'lodash';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import {
  apiGetStatisticsCustomerByDay,
  apiGetStatisticsData
} from '@services/ManageCustomers/CustomerServices';
import { ADMIN_ROLE } from '@utils/constant';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import dayjs from 'dayjs';
import { useRole } from '@contexts/Auth/UseRole';

const { Option } = Select;

const { RangePicker } = DatePicker;

export default function List() {
  const [data, setData] = useState([]);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState('DT');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeDateString, setRangeDateString] = useState([
    moment().subtract(1, 'day').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD')
  ]);
  const [serialNo, setSerialNo] = useState(null);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'day'), // Start date: 1 day ago
    dayjs() // End date: today
  ]);
  const { isWebAdmin, isWebManager, isWebSuperAdmin } = useRole();
  const fetchData = async () => {
    try {
      setSelectedRowKey(null);
      setSelectedRow(null);
      setLoadingFetch(true);
      const res = await apiGetStatisticsCustomerByDay({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        serialNo: serialNo,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        fromDate: rangeDateString?.length ? rangeDateString[0] : undefined,
        toDate: rangeDateString?.length ? rangeDateString[1] : undefined
      });
      setData(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError({ message: 'Something went wrong!' });
      console.log(err);
    } finally {
      setLoadingFetch(false);
    }
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onChangeDateRange = (dates, dateStrings) => {
    setRangeDateString(dateStrings);
    setDateRange(dates);
  };

  // useEffect(() => {
  //   fetchData();
  // }, [currentPage, pageSize, flagSearch]);

  const columns = [
    {
      title: 'Số Sêri Module',
      dataIndex: 'serial_no',
      key: 'serial_no',
      width: 150
    },
    {
      title: 'Số IMEI Module',
      dataIndex: 'imei',
      key: 'imei',
      width: 180
    },
    {
      title: 'Ngày thống kê',
      dataIndex: 'denseData_startCollectionTime',
      key: 'denseData_startCollectionTime',
      width: 150
    },
    {
      title: 'Chỉ số tại thời điểm 00:00 (lít)',
      dataIndex: 'start_meter_reading_at_day',
      key: 'start_meter_reading_at_day',
      render: (_, record) => <>{formatCurrency(record?.start_meter_reading_at_day)}</>,
      width: 150
    },
    {
      title: 'Tiêu thụ từ 00:00 tới 04:00 (lít)',
      dataIndex: 'water_consumption_of_the_day',
      key: 'water_consumption_of_the_day',
      render: (_, record) => <>{formatCurrency(record?.water_consumption_of_the_day)}</>,
      width: 120
    },
    {
      title: 'Lượng nước tiêu thụ từ 00:00 - 04:00 qua các khoảng 5 phút (lít)',
      children: [
        ...Array.from({ length: 48 }, (_, index) => {
          const hour = Math.floor((index + 1) / 12);
          const minute = ((index + 1) % 12) * 5;
          const timeLabel = `${hour.toString().padStart(2, '0')}:${minute
            .toString()
            .padStart(2, '0')}`;
          return {
            title: timeLabel,
            dataIndex: `minute${index + 1}`,
            key: `minute${index + 1}`,
            width: 80,
            render: (_, record) => (
              <>{formatCurrency(record?.diffirenceOfTheFlowWaterMeter?.[index + 1])}</>
            )
          };
        })
      ]
    }
  ];

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    let user = loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH);
    if (!isEmpty(user)) {
      user = JSON.parse(user);
    }
    setLoading(true);
    const response = await axios.get(
      `${URL_API}/api/customer/v2/statistics-customer-by-day/export`,
      {
        params: {
          keyWord: search ?? undefined,
          serialNo: serialNo,
          community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
          fromDate: rangeDateString?.length ? rangeDateString[0] : undefined,
          toDate: rangeDateString?.length ? rangeDateString[1] : undefined
        },
        headers: {
          Authorization: `Bearer ${user?.accessToken}`
        },
        responseType: 'blob'
      }
    );
    const dateKey = moment().format('YYYYMMDD.HHmmss');
    const type = response.headers['content-type'];
    const blob = new Blob([response.data], { type: type });
    const link1 = document.createElement('a');
    link1.href = window.URL.createObjectURL(blob);
    link1.setAttribute('download', `Thong_ke_tieu_thu_theo ngày_${dateKey}.xlsx`);
    document.body.appendChild(link1);
    link1.click();
    setLoading(false);
  };
  const [loadingSync, setLoadingSync] = useState(false);
  const handleSyncButtonClick = async () => {
    try {
      setLoadingSync(true);
      const res = await apiSyncTimeData();
      // message.success('Cập nhật thành công !');
      fetchData();
    } catch (e) {
      console.log(e);
      message.error('Có lỗi xảy ra !');
    } finally {
      setLoadingSync(false);
    }
  };

  const handleRowClick = async (row) => {
    if (!row?.diffirenceOfTheFlowWaterMeter?.length) {
      showError('Khách hàng không có dữ liệu ngày hôm nay!');
      return;
    }
    setSelectedRowKey(row?.stt);
    setSelectedRow(row);
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    });
  };

  const dataCharts = [
    {
      name: 'Tiêu thụ giờ',
      data: selectedRow ? selectedRow?.diffirenceOfTheFlowWaterMeter : [],
      type: 'bar'
    }
  ];

  // useEffect(() => {
  //   handleSyncButtonClick();
  // }, []);

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Dữ liệu kiểm tra rò rỉ
          </Typography.Title>
        </Col>
        {(isWebAdmin || isWebManager || isWebSuperAdmin) && (
          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportExcelButtonClick}
              loading={loading}
              style={{
                backgroundColor: '#EAF5EF',
                borderColor: '#EAF5EF',
                color: '#0174CF',
                height: '36px',
                fontWeight: '500'
              }}>
              Xuất báo cáo
            </Button>
          </Col>
        )}
      </Row>
      <Card>
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col md={4} xs={6}>
            <Input
              style={{ height: '36px', width: '100%', fontSize: '18px' }}
              size="small"
              placeholder="Tìm kiếm theo số sêri, số IMEI..."
              value={search}
              onChange={handleSearchChange}
            />
          </Col>
          <Col md={6} xs={8}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col md={4} xs={6}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                setCurrentPage(1);
                setFlagSearch(!flagSearch);
              }}
              style={{
                height: '36px',
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                fontSize: '18px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          loading={loadingFetch}
          dataSource={data || []}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            onChange: handleChangePage,
            onShowSizeChange: handleChangePageSize,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: {
              cursor: 'pointer',
              backgroundColor: selectedRowKey === record.stt ? '#e6f7ff' : undefined
            }
          })}
          style={{
            borderRadius: '4px',
            thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
            fontSize: '30px'
          }}
          scroll={{ x: 'max-content', y: 400 }}
        />
      </Card>
      {selectedRowKey && (
        <>
          <Col>
            <Typography.Title level={4} style={{ marginTop: '8px' }}>
              Biểu đồ thống kê tiêu thụ trong khoảng thời gian từ 00:05 - 04:00
            </Typography.Title>
          </Col>
          <Card>
            <Typography.Title style={{ fontSize: '16px' }}>
              {`Thiết bị ${selectedRow?.serial_no}`}
            </Typography.Title>
            <Chart
              options={{
                colors: COLORS,
                chart: {
                  zoom: {
                    enabled: false
                  },
                  toolbar: {
                    show: true
                  }
                },
                plotOptions: {
                  bar: {
                    horizontal: false,
                    columnWidth: '25px',
                    borderRadius: 4
                  }
                },
                fill: {
                  opacity: 1
                },
                dataLabels: {
                  enabled: false
                },
                stroke: {
                  show: true,
                  width: 2,
                  colors: ['transparent']
                },
                labels: Array.from({ length: 48 }, (_, index) => {
                  const totalMinutes = 5 * (index + 1);
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;
                  return `${hours.toString().padStart(2, '0')}:${minutes
                    .toString()
                    .padStart(2, '0')}`;
                }),
                xaxis: {
                  labels: {
                    rotate: -45, // Rotate labels to be angled
                    style: {
                      fontSize: '13px'
                    }
                  }
                },
                yaxis: {
                  show: true
                },
                grid: {
                  show: true
                },
                tooltip: {
                  y: {
                    formatter: (val) => `${formatCurrency(val)} lít`
                  }
                },
                legend: {
                  horizontalAlign: 'left'
                }
              }}
              type="bar"
              series={dataCharts}
              height={400}
            />
          </Card>
        </>
      )}
    </div>
  );
}
